import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { Observable, from } from 'rxjs';


import { AuthService } from '../auth/auth.service';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { Member } from '../interfaces/member';
import { Ticket } from '../interfaces/Ticket';
import { Evaluation } from '../interfaces/evaluation';
import { authState } from '@angular/fire/auth';
import { CallerIdVerificationResult, CallerIdVerificationStart, CopyOutcome, CopyTarget, OutboundNumberRef, OutboundNumbersBatchResult, OutboundNumbersView } from '../interfaces/outbound-numbers';

interface CalculateCostsParams {
  minDuration: number;
  maxDuration: number;
  prices: any;
  callType: 'inbound' | 'outbound';
  playbookId?: string;
  callId?: string;
}

export interface BillyConsumptionBreakdownItem {
  rule_id: string;
  assistant_id: string;
  assistant_name: string;
  billing_type: string;
  price_per_unit: number;
  minimum: number;
  units: number;
  unit_label: string;
  subtotal: number;
  detail?: Record<string, any>;
  // v2 rule items carry a canonical unit code and the rule name.
  unit?: 'seconds' | 'minutes' | 'calls';
  rule_name?: string | null;
  // Proyecto (cliente final) al que pertenece el agente, p.ej. "NATURGY". Lo
  // deriva el servidor y es sólo presentación: no entra en ningún importe.
  // Ausente en la vista de admin, donde cada línea ya es un cliente.
  project?: string | null;
}

export interface BillyMinutesPackage {
  contracted: number;
  consumed: number;
  remaining: number;
  usage_percent: number;
  over_allowance: boolean;
}

// Balance block for rules-v2 profiles: either the committed volume tier of the
// active contract or the prepaid bag. Overage on a committed tier is billable
// consumption (charged next period at the tier price), not an error state.
export type BillyBalanceInfo =
  | {
      kind: 'committed_tier';
      unit: 'seconds' | 'minutes' | 'calls';
      tier_name: string | null;
      contracted_units: number;
      consumed_units: number;
      remaining_units: number;
      overage_units: number;
      overage_amount: number;
      price_per_unit: number;
      usage_percent: number;
    }
  | {
      kind: 'prepaid_bag';
      unit: string;
      // Saldo en euros a precio de tarifa y su equivalente en unidades al
      // precio de la bolsa. Los *_eur pueden faltar en un servidor anterior.
      available_eur?: number;
      available_units: number;
      consumed_period_eur?: number;
      consumed_period_units: number | null;
      price_per_unit?: number | null;
      // Barra de saldo: tamaño de la bolsa actual, % que queda y fin de vigencia.
      bag_size_eur?: number | null;
      remaining_percent?: number | null;
      valid_until?: string | null;
      payment_modality: string;
      discount_percent: number;
    };

export type BillyConsumptionData =
  | {
      visible: false;
      reason: 'missing_billy_profile' | 'missing_billing_rules';
    }
  | {
      visible: true;
      view_mode?: 'client_agents' | 'admin_clients';
      client_name: string;
      currency: string;
      period_label: string;
      period_mode: 'current' | 'custom';
      billing_period_start_day: number;
      period_start_date: string;
      period_end_date: string;
      period_start: string;
      period_end: string;
      subtotal: number;
      minimum_applied: number;
      correction_factor: number;
      correction_amount: number;
      total: number;
      breakdown: BillyConsumptionBreakdownItem[];
      // Only present for client views when the client has a contracted minutes
      // package configured (ticket #126). Null/absent means no bag configured.
      minutes_package?: BillyMinutesPackage | null;
      balance?: BillyBalanceInfo | null;
      // Vista parcial: un member que solo ve algunos agentes (o un workspace).
      // El backend quita total, mínimo, descuento, paquete y bolsa, y manda la
      // suma de sus líneas.
      partial?: boolean;
      partial_scope?: string | null;
      partial_lines_total?: number;
    };

export interface BillyConsumptionResponse {
  success: boolean;
  data: BillyConsumptionData;
  error?: string;
}

export interface BillyBillingHistoryMonth {
  key: string;
  label: string;
  total: number;
}

export interface BillyBillingHistoryResponse {
  success: boolean;
  data: {
    currency: string;
    months: BillyBillingHistoryMonth[];
  };
  error?: string;
}

export interface BillyConsumptionFilters {
  startDate?: string;
  endDate?: string;
}


/** Una rutina personal de un member, tal como la devuelve el backend antes de darle de baja. */
export interface MemberRoutine {
  id: string;
  name: string;
  enabled: boolean;
  schedule_preset: string;
  instructions: string;
}

export interface MemberRoutines {
  owned: MemberRoutine[];
  editing: Array<{ id: string; name: string; scope: string }>;
}

export type MemberRoutineAction = { id: string; action: 'move_account' | 'move_workspace' | 'delete'; workspace_id?: string };

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private url = environment.server;
  private authToken: string = '';
  private static interceptorSetup = false;

  constructor(private authService: AuthService, private http: HttpClient) {
    // Inicializar el token al construir el servicio
    this.refreshAuthToken();
    
    // Suscribirse a cambios en la autenticación usando authState del Auth service
    authState(this.authService.auth).subscribe(() => {
      this.refreshAuthToken();
    });

    // Setup axios interceptor only once (before any API calls)
    this.setupAxiosInterceptor();
  }

  /**
   * Setup axios interceptor to automatically add impersonation headers to ALL requests
   * Reads directly from sessionStorage to ensure it works even before services are fully initialized
   */
  private setupAxiosInterceptor(): void {
    if (ApiService.interceptorSetup) {
      return; // Already configured
    }
    
    axios.interceptors.request.use(
      (config) => {
        // Read impersonation data directly from sessionStorage
        // This ensures it works even if ImpersonationService hasn't been injected yet
        try {
          const storedData = sessionStorage.getItem('meetzy_impersonation');
          if (storedData) {
            const impersonatedUser = JSON.parse(storedData);
            if (impersonatedUser && impersonatedUser.id) {
              config.headers = config.headers || {};
              config.headers['X-Impersonate-User-Id'] = impersonatedUser.id;
              config.headers['X-Impersonate-User-Type'] = impersonatedUser.type;
              if (impersonatedUser.companyId) {
                config.headers['X-Impersonate-Company-Id'] = impersonatedUser.companyId;
              }
              console.log('🎭 Axios: Impersonating', impersonatedUser.email, '(ID:', impersonatedUser.id, ')');
            }
          }
        } catch (e) {
          console.error('🎭 Axios interceptor error reading sessionStorage:', e);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    ApiService.interceptorSetup = true;
    console.log('🎭 Axios interceptor configured for impersonation');
  }

  // Método para actualizar el token
  private async refreshAuthToken(): Promise<void> {
    try {
      const userData = await this.authService.getUserData();
      if (userData) {
        this.authToken = await userData.getIdToken();
      } else {
        this.authToken = '';
      }
    } catch (error) {
      console.error('Error refreshing auth token:', error);
      this.authToken = '';
    }
  }

  // Método helper para obtener headers con autenticación
  // Note: Impersonation headers are automatically added by axios interceptor
  private async getAuthHeaders(): Promise<any> {
    await this.refreshAuthToken();
    return {
      'Authorization': `Bearer ${this.authToken}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Validate and start impersonation for a user/member
   * @param targetUserId - The ID of the user to impersonate
   * @param targetType - Whether it's a 'user' or 'member'
   */
  async validateImpersonation(targetUserId: string, targetType: 'user' | 'member'): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/impersonate/validate`, {
          targetUserId,
          targetType
        }, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error validating impersonation:', error);
        throw error;
      }
    }
    throw new Error('User not authenticated');
  }

  async isPartner() {
    let userData = await this.authService.getUserData()

    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());

      let partner = await axios.get(`${this.url}/dashboard/is_partner`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      //console.log("playbooks.data: ", playbooks.data);
      return partner.data;
    } else {

      return []
    }
  }

  async getPlaybooks(demo: boolean | undefined, includeArchived: boolean = false) {
    let userData = await this.authService.getUserData()
    if (userData) {
      if (demo || demo == undefined) {
        demo = true
      } else {
        demo = false
      }
      // Ticket #290: include archived agents when requested (e.g. "Show archived" toggle)
      const archivedParam = includeArchived ? '&include_archived=true' : '';
      let playbooks = await axios.get(`${this.url}/dashboard/playbooks?demos=${demo}${archivedParam}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      //console.log("playbooks.data: ", playbooks.data);
      return playbooks.data;
    } else {
      return []
    }
  }

  async archivePlaybook(playbookId: string, releasePhone: boolean = false) {
    const userData = await this.authService.getUserData();
    if (!userData) {
      throw new Error('User not authenticated');
    }

    const response = await axios.patch(`${this.url}/dashboard/playbooks/${playbookId}/archive`, {
      release_phone: releasePhone
    }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  }

  // Ticket #290: restore a previously archived agent
  async unarchivePlaybook(playbookId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) {
      throw new Error('User not authenticated');
    }

    const response = await axios.patch(`${this.url}/dashboard/playbooks/${playbookId}/unarchive`, {}, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  }

  async getPlaybooksForBulkCalls() {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        let response = await axios.get(`${this.url}/dashboard/playbooks-for-bulk-calls`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        console.error("Error fetching playbooks for bulk calls:", error);
        return [];
      }
    } else {
      return []
    }
  }

  async getPlaybooksFromCompany(companyId: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        // Get all playbooks and filter by company ID on the frontend
        // Since the backend doesn't support company filtering, we'll get all demos and filter
        let playbooks = await axios.get(`${this.url}/dashboard/playbooks?demos=true`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Filter playbooks by company ID (using user_id as it represents the company/owner)
        const filteredPlaybooks = playbooks.data.filter((playbook: any) => 
          playbook.user_id === companyId
        );
        
        return filteredPlaybooks;
      } catch (error) {
        console.error('Error fetching playbooks from company:', error);
        return [];
      }
    } else {
      return []
    }
  }

  async getPlaybookDangerZone(data: any) {
    let userData = await this.authService.getUserData();
    if (userData) {
        let playbooks = await axios.get(`${this.url}/dashboard/playbook/danger?id=${data.id}`, {
            headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
            }
        });
        return playbooks.data;
    } else {
        return [];
    }
  }

  async putPlaybookDangerZone(data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      // The backend now responds as soon as the prompt + core fields are
      // persisted (vars/functioncalls/ElevenLabs sync are deferred), so this
      // request should normally return in well under a second. We still set a
      // generous explicit timeout so a network/proxy stall surfaces as a real
      // axios error in the dashboard instead of an indefinite "Saving..."
      // spinner.
      let playbook = await axios.put(`${this.url}/dashboard/playbook/danger`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        timeout: 90000
      });
      return playbook.data;
    } else {
      return []
    }
  }

  async updatePlaybookFields(playbookId: string, fields: any[]) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const response = await axios.put(`${this.url}/dashboard/playbook/${playbookId}/fields`, 
        { fields }, 
        {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } else {
      return []
    }
  }

  async getCompanies() {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let companies = await axios.get(`${this.url}/dashboard/companies`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      //console.log("playbooks.data: ", playbooks.data);
      return companies.data;
    } else {
      return []
    }
  }

  async getMembers() {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let members = await axios.get(`${this.url}/dashboard/members`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      //console.log("playbooks.data: ", playbooks.data);
      return members.data;
    } else {
      return []
    }
  }



  async isUserMember(): Promise<boolean> {
    let userData = await this.authService.getUserData();
    if (userData) {
      let response = await axios.get(`${this.url}/dashboard/isMember`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.isMember;
    } else {
      return false;
    }
  }

  async getTickets(company?: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const params = new URLSearchParams();
      if (company && company.trim() !== '') {
        params.append('company', company);
      }
      
      const url = `${this.url}/dashboard/tickets${params.toString() ? '?' + params.toString() : ''}`;
      
      let tickets = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return tickets.data;
    } else {
      return []
    }
  }

  async getTicketCompanies() {
    let userData = await this.authService.getUserData()
    if (userData) {
      let companies = await axios.get(`${this.url}/dashboard/tickets/companies`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return companies.data;
    } else {
      return []
    }
  }

  async isUserAdmin(): Promise<boolean> {
    try {
      // Try to call the companies endpoint - if it succeeds, user is admin
      await this.getTicketCompanies();
      return true;
    } catch (error) {
      // If it fails with 403, user is not admin
      return false;
    }
  }

  async postMember(data: Member) {
    let userData = await this.authService.getUserData()
    if (userData) {

      //CONVERT PLAYBOOKS TO COMMA-SEPARATED OF STRINGS
      let playbooks = data.playbooks?.join(',');
      data.playbooks = undefined;
      data.assistants = playbooks;

      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let members = await axios.post(`${this.url}/dashboard/member`, {
        ...data,
        can_view_recordings: data.can_view_recordings,
        send_email: data.send_email ?? true
      }, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return members.data;
    } else {
      return []
    }
  }

  async putMember(data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {

      //CONVERT PLAYBOOKS TO COMMA-SEPARATED OF STRINGS
      let playbooks = data.playbooks?.join(',');
      data.playbooks = undefined;
      data.assistants = playbooks;

      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let members = await axios.put(`${this.url}/dashboard/member`, {
        ...data,
        can_view_recordings: data.can_view_recordings,
        company_id: data.company_id // Include company_id for admin operations
      }, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return members.data;
    } else {
      return []
    }
  }

  async putUser(data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.put(`${this.url}/dashboard/users/update`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      return { error: 'User not authenticated' }
    }
  }

  /**
   * `routineActions`: qué hacer con cada rutina personal del member (llevarla
   * a la cuenta, a un workspace o borrarla). Las que no vengan pasan a la
   * cuenta en pausa (lo decide el backend).
   */
  async deleteMember(id: any, routineActions?: MemberRoutineAction[]) {
    console.log("deleteMember", id)
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let members = await axios.delete(`${this.url}/dashboard/member`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: {
          id: id
        },
        ...(routineActions && routineActions.length > 0 ? { data: { routine_actions: routineActions } } : {})
      });
      return members.data;
    } else {
      console.log("no user data")
      return []
    }
  }

  /**
   * Rutinas personales de un member (y las que edita), antes de darle de
   * baja. El staff de Meetzy pasa `companyId` desde Companies. Un backend
   * anterior a las rutinas por ámbito devuelve 404: el caller lo trata como
   * «no se sabe» y no bloquea la baja.
   */
  async getMemberRoutines(memberId: string, companyId?: string): Promise<MemberRoutines> {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.get(`${this.url}/dashboard/monitoring-routines/member/${memberId}`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      },
      ...(companyId ? { params: { company_id: companyId } } : {})
    });
    return {
      owned: Array.isArray(response.data?.owned) ? response.data.owned : [],
      editing: Array.isArray(response.data?.editing) ? response.data.editing : []
    };
  }

  /** Workspaces (sub-clientes) de la propia cuenta. */
  async getWorkspaces(): Promise<Array<{ id: string; name: string }>> {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.get(`${this.url}/dashboard/workspaces`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return Array.isArray(response.data?.workspaces) ? response.data.workspaces : [];
  }

  /**
   * Sync Firebase claims with ClickHouse data for a specific member
   * Useful when permissions get out of sync between Firebase and ClickHouse
   */
  async syncMemberClaims(email: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      console.log(`🔄 Syncing claims for member: ${email}`);
      
      let response = await axios.post(`${this.url}/dashboard/member/sync-claims`, {
        email: email
      }, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Claims synced successfully:', response.data);
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Promote a member to full account admin, or demote an admin back to a
   * regular member. Meetzy staff pass `companyId` to act on any account from
   * the /companies page; account owners/admins omit it to act on their own.
   */
  async updateMemberRole(id: string, role: 'admin' | 'member', companyId?: string) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.put(`${this.url}/dashboard/member/role`, { id, role, ...(companyId ? { company_id: companyId } : {}) }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Meetzy staff only: remove a member from a client account (Companies view).
   * Same removal as the owner's Remove in /members.
   */
  async removeCompanyMember(id: string, companyId: string) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.delete(`${this.url}/dashboard/main/company-member`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      },
      params: { id, company_id: companyId }
    });
    return response.data;
  }

  /**
   * Meetzy staff only: companies that look abandoned (no login, no team, no
   * agents, no calls in 90 days), each with the reasons. Read only: nothing is
   * archived by asking.
   */
  async getArchiveCandidates() {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.get(`${this.url}/dashboard/companies/archive-candidates`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Meetzy staff only: archive (hides the company from lists and selectors) or
   * restore companies. A reason is required to archive. If some of them still
   * have agents or recent calls the server answers 409 `needs_confirmation`;
   * repeat with `confirmActive: true` once the person has agreed.
   */
  async setCompaniesArchived(
    ids: string[],
    archived: boolean,
    reason: string,
    options: { source?: 'suggestion' | 'manual'; confirmActive?: boolean } = {}
  ) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.put(`${this.url}/dashboard/companies/archive`, {
      ids,
      archived,
      reason,
      source: options.source ?? 'manual',
      ...(options.confirmActive ? { confirm_active: true } : {})
    }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Meetzy staff only: who archived/restored a company, when and why. */
  async getCompanyStatusHistory(companyId: string) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.get(`${this.url}/dashboard/companies/${encodeURIComponent(companyId)}/status-history`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Change a teammate's email on their behalf (owner/admin action). Updates
   * Firebase Auth and the database atomically on the backend.
   */
  async updateMemberEmail(id: string, newEmail: string) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.put(`${this.url}/dashboard/member/email`, { id, newEmail }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Resend the password-setup invitation to a member (e.g. the first one
   * failed to deliver, or the link expired).
   */
  async resendMemberInvite(email: string) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.post(`${this.url}/dashboard/member/resend-invite`, { email }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Self-service: fetch the signed-in user's own profile to prefill the
   * Account settings form.
   */
  async getAccountProfile() {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.get(`${this.url}/dashboard/account/profile`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`
      }
    });
    return response.data;
  }

  /**
   * Self-service: change the signed-in user's own email. The caller must
   * have reauthenticated moments before (fresh ID token) or the backend
   * rejects the request with a REAUTH_REQUIRED error.
   */
  async updateOwnEmail(newEmail: string) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    // Force a fresh token so the backend sees a recent auth_time.
    let response = await axios.put(`${this.url}/dashboard/account/email`, { newEmail }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken(true)}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Self-service: update the signed-in user's own profile (name/company for
   * owners, name/job title for members).
   */
  async updateAccountProfile(data: { fullname?: string; company?: string; name?: string; job_title?: string; news_emails?: boolean }) {
    let userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    let response = await axios.put(`${this.url}/dashboard/account/profile`, data, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  async updateUserPassword(email: string, password: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.put(`${this.url}/dashboard/users/update-password`, {
        email: email,
        password: password
      }, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      return { error: 'User not authenticated' }
    }
  }

  getRecording(data: any): Observable<Blob> {
    let userData = this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      return from(userData.getIdToken()).pipe(

        switchMap((token) => {
          const headers = new HttpHeaders({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          });
          return this.http.get(`${this.url}/dashboard/recording`, {
            headers: headers,
            responseType: 'blob',
            params: {
              call_id: data.call_id
            }
          });
        })
      );
      /*return axios.get(`${this.url}/dashboard/recording`,{
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params:data
      });*/

    } else {
      //RETURN A EMPTY OBSERVABLE
      return new Observable();
    }
  }

  /*async getPlaybooksDemo() {
    let userData=await this.authService.getUserData()
    if(userData){
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let playbooks = await axios.get(`${this.url}/dashboard/playbooks?demos=true`,{
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      //console.log("playbooks.data: ", playbooks.data);
      return playbooks.data;
    }else{
      return []
    }
  }*/

  // Ticket #526/#500/#491: server-side streaming export (see /dashboard/calls/export
  // in meetzy-ai-server). Mirrors the Blob-download pattern already used by
  // getRecording() below, since a plain <a href> can't carry the Authorization header.
  getCallsExport(params: any): Observable<Blob> {
    let userData = this.authService.getUserData()
    if (userData) {
      return from(userData.getIdToken()).pipe(
        switchMap((token) => {
          const headers = new HttpHeaders({
            'Authorization': `Bearer ${token}`,
          });
          return this.http.get(`${this.url}/dashboard/calls/export`, {
            headers: headers,
            responseType: 'blob',
            params: params
          }) as Observable<Blob>;
        })
      );
    } else {
      return new Observable();
    }
  }

  async getRecallFamily(callId: string): Promise<string[]> {
    const userData = await this.authService.getUserData();
    if (!userData) return [];
    const response = await axios.get(`${this.url}/dashboard/call/${callId}/recall-family`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    const ids = response.data?.ids;
    return Array.isArray(ids) ? ids.map((id: unknown) => String(id)).filter(Boolean) : [];
  }

  async getCalls(params: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let calls = await axios.get(`${this.url}/dashboard/calls`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: params
      });
      return calls.data;
    } else {
      return []
    }
  }

  async getCallsAggregate(params: any): Promise<{total: number, closed: number, voicemail: number, avg_duration: number | null, avg_latency: number | null, p90_latency: number | null, transfer_seconds_inbound: number, transfer_seconds_outbound: number}> {
    const userData = await this.authService.getUserData();
    if (!userData) return { total: 0, closed: 0, voicemail: 0, avg_duration: null, avg_latency: null, p90_latency: null, transfer_seconds_inbound: 0, transfer_seconds_outbound: 0 };
    const resp = await axios.get(`${this.url}/dashboard/calls/aggregate`, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' },
      params,
    });
    return resp.data;
  }

  // Nombres de evaluación de los agentes del usuario (agrupados) para el filtro de /calls
  async getEvaluationCatalog(assistantIds: string[] = []): Promise<Array<{ name: string; assistants: number }>> {
    const userData = await this.authService.getUserData();
    if (!userData) return [];
    const params: any = {};
    if (assistantIds.length > 0) params.assistantId = assistantIds.join(',');
    const resp = await axios.get(`${this.url}/dashboard/evaluations-catalog`, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' },
      params,
    });
    return Array.isArray(resp.data) ? resp.data : [];
  }

  // Preferencias de tabla configurable por usuario (columnas visibles/orden),
  // usadas por /calls-v2. tableKey identifica la tabla (ej. "calls-v2").
  async getTablePreferences(tableKey: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.get(`${this.url}/dashboard/table-preferences/${tableKey}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data; // { config: any|null, updated_time: string|null }
    } else {
      return { config: null, updated_time: null };
    }
  }

  async saveTablePreferences(tableKey: string, config: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.put(`${this.url}/dashboard/table-preferences/${tableKey}`, config, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data; // { config, updated_time }
    }
    throw new Error('Not authenticated');
  }

  // Estado de revisión de auditoría por llamada (marcado "revisado" + nota),
  // compartido entre todo el equipo — usado por /calls-v2.
  async getCallReviews(callIds: string[]) {
    let userData = await this.authService.getUserData()
    if (userData && callIds.length > 0) {
      // POST + body: a full day of calls can be 500-1000+ ids, too long for
      // a comma-joined querystring (a GET with ~30KB of ids failed outright
      // with a plain "Network Error", no server response at all).
      let response = await axios.post(`${this.url}/dashboard/call-reviews`, { ids: callIds }, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data; // { reviews: { [callId]: { reviewed, note, reviewed_by, updated_time } } }
    }
    return { reviews: {} };
  }

  async saveCallReview(callId: string, reviewed: boolean, note: string, verdict: 'ok' | 'ko' | null = null) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.put(`${this.url}/dashboard/call/${callId}/review`, { reviewed, note, verdict }, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data; // { review }
    }
    throw new Error('Not authenticated');
  }

  // Historial versionado de una review (quién/cuándo puso cada OK/KO/nota),
  // más antiguo primero — poblado por el trigger call_review_notes_audit.
  async getCallReviewHistory(callId: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.get(`${this.url}/dashboard/call/${callId}/review/history`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data; // { history: [{ id, reviewed, verdict, note, changed_by, changed_at }] }
    }
    return { history: [] };
  }

  // ---------------------------------------------------------------------
  // AI evolution loop
  //
  // El bucle nocturno lee los OK/KO y las notas que deja el equipo, propone
  // una mejora del prompt y — solo si TODOS los tests del agente pasan —
  // deja una versión candidata sin desplegar. Aquí solo están las dos
  // puertas humanas: encender el bucle por agente y desplegar la versión.
  // ---------------------------------------------------------------------

  /** Estado del bucle para un agente (activado, elegible, nº de tests, últimas pasadas). */
  async getEvolutionAgentState(assistantId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const response = await axios.get(`${this.url}/dashboard/evolution/agent/${assistantId}`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Enciende/apaga el bucle. Solo permitido en agentes avanzados. */
  /**
   * "Mejoralo ahora": lanza la misma pasada que corre de noche. Responde 202 y
   * sigue por su cuenta (puede tardar decenas de minutos); el progreso se ve
   * en `manual_run` de getEvolutionAgentState.
   */
  async runEvolutionNow(assistantId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const response = await axios.post(`${this.url}/dashboard/evolution/agent/${assistantId}/run`, {}, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  async setEvolutionAgentEnabled(assistantId: string, enabled: boolean) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const response = await axios.put(`${this.url}/dashboard/evolution/agent/${assistantId}`, { enabled }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Propuestas listas que este usuario aún no ha visto (alimenta el modal). */
  async getEvolutionPending() {
    const userData = await this.authService.getUserData();
    if (!userData) return { count: 0, proposals: [] };
    const response = await axios.get(`${this.url}/dashboard/evolution/pending`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Detalle: evidencia, diff del prompt y resultado de los tests. */
  async getEvolutionProposal(proposalId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const response = await axios.get(`${this.url}/dashboard/evolution/proposals/${proposalId}`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Marca el aviso como visto por este usuario (no decide nada sobre la propuesta). */
  async markEvolutionProposalSeen(proposalId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) return { ok: false };
    const response = await axios.post(`${this.url}/dashboard/evolution/proposals/${proposalId}/seen`, {}, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Puerta humana nº2: desplegar la versión candidata a producción. */
  async deployEvolutionProposal(proposalId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const response = await axios.post(`${this.url}/dashboard/evolution/proposals/${proposalId}/deploy`, {}, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /** Historial de propuestas del usuario (pendientes primero): status=all|ready|decided. */
  async listEvolutionProposals(status: 'all' | 'ready' | 'decided' = 'all') {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const response = await axios.get(`${this.url}/dashboard/evolution/proposals?status=${status}`, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` }
    });
    return response.data;
  }

  /**
   * Deshacer una versión desplegada por el bucle: vuelve a producción la
   * versión anterior por el despliegue completo. Solo si producción sigue
   * siendo la versión de la propuesta.
   */
  async rollbackEvolutionProposal(proposalId: string, reason?: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const body = reason && reason.trim() ? { reason: reason.trim().slice(0, 600) } : {};
    const response = await axios.post(`${this.url}/dashboard/evolution/proposals/${proposalId}/rollback`, body, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      },
      timeout: 60000
    });
    return response.data;
  }

  /**
   * Descartar la propuesta. La versión candidata se queda en el historial. El
   * motivo es opcional pero la siguiente pasada del bucle lo lee para no volver
   * a proponer lo mismo.
   */
  async discardEvolutionProposal(proposalId: string, reason?: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const body = reason && reason.trim() ? { reason: reason.trim().slice(0, 600) } : {};
    const response = await axios.post(`${this.url}/dashboard/evolution/proposals/${proposalId}/discard`, body, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  async magicSearch(params: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      //console.log("${this.url}: ",this.url)
      let calls = await axios.get(`${this.url}/dashboard/calls/search`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: params
      });
      //console.log("playbooks.data: ", playbooks.data);
      return calls.data;
    } else {
      return []
    }
  }

  async getMessages(params: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      //console.log("${this.url}: ",this.url)
      let messages = await axios.get(`${this.url}/dashboard/call/${params.call_id}/messages`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      //console.log("playbooks.data: ", playbooks.data);
      return messages.data;
    } else {
      return []
    }
  }

  // Turno a turno en crudo desde ElevenLabs (conversation_turn_metrics,
  // llm_usage, tool_calls, tool_results) — no persistido en BBDD, se pide en
  // vivo; puede devolver 404 si ElevenLabs ya purgó la conversación.
  async getElevenLabsTranscript(callId: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/call/${callId}/elevenlabs-transcript`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data; // { transcript: TranscriptEntry[] }
    }
    return { transcript: [] };
  }

  async getTokens(params: any) {
    //console.log("getTokens")
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      if (userData) {
        let tokens = await axios.get(`${this.url}/dashboard/tokens`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          params: params
        });
        //console.log("playbooks.data: ", playbooks.data);
        return tokens.data;
      } else {
        //console.log("no user data")
        return []
      }
    } else {
      //console.log("not logged in")
      return []
    }
  }

  async createToken(data: { name: string; expires_in_days?: number }) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      if (userData) {
        let response = await axios.post(`${this.url}/dashboard/tokens`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      }
    }
    throw new Error('Not authenticated');
  }

  async deleteToken(tokenId: string) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      if (userData) {
        let response = await axios.delete(`${this.url}/dashboard/tokens/${tokenId}`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      }
    }
    throw new Error('Not authenticated');
  }

  async exchangeAuthCode(data: any) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      if (userData) {
        let tokens = await axios.post(`${this.url}/dashboard/auth_code`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        //console.log("playbooks.data: ", playbooks.data);
        return tokens.data;
      } else {
        //console.log("no user data")
        return []
      }
    } else {
      //console.log("not logged in")
      return []
    }
  }

  async postIntegration(data: any) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      //console.log("userData: ",userData)
      if (userData) {
        //console.log("userData.getIdToken(): ", await userData.getIdToken());
        let integration = await axios.post(`${this.url}/dashboard/integrations`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        console.log("playbooks.data: ", integration.data);
        return integration.data;
      } else {
        return []
      }
    }
  }

  async postCall(data: any) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      //console.log("userData: ",userData)
      if (userData) {
        //console.log("userData.getIdToken(): ", await userData.getIdToken());
        let url = this.url+'/dashboard/exec-playbook'

        /*if(data.is_low_latency){
          url = 'https://flark-low-latency-9123e42a8f93.herokuapp.com/call'
        }*/

        let call = await axios.post(url, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return call;
      } else {
        return { status: 500, data: [] }
      }
    }
    else {
      return { status: 500, data: [] }
    }
  }








  async getIntegrations(params: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let integrations = await axios.get(`${this.url}/dashboard/integrations`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: params
      });
      //console.log("playbooks.data: ", playbooks.data);
      return integrations.data;
    } else {
      console.log("no user data")
      return []
    }
  }

  async getPhoneNumbers() {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let integrations = await axios.get(`${this.url}/dashboard/numbers`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: []
      });
      //console.log("playbooks.data: ", playbooks.data);
      return integrations.data;
    } else {
      console.log("no user data")
      return []
    }
  }

  async deleteIntegration(data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let integrations = await axios.delete(`${this.url}/dashboard/integrations`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: data
      });
      //console.log("playbooks.data: ", playbooks.data);
      return integrations.data;
    } else {
      console.log("no user data")
      return []
    }
  }

  async putExternalFeedback(data: any) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      //console.log("userData: ",userData)
      if (userData) {
        //console.log("userData.getIdToken(): ", await userData.getIdToken());
        let feedback = await axios.put(`${this.url}/dashboard/externalFeedback`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        console.log("feedback.data: ", feedback.data);
        return feedback.data;
      } else {
        return []
      }
    }
  }

  async putInternalFeedback(data: any) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      //console.log("userData: ",userData)
      if (userData) {
        //console.log("userData.getIdToken(): ", await userData.getIdToken());
        let feedback = await axios.put(`${this.url}/dashboard/internalFeedback`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        console.log("feedback.data: ", feedback.data);
        return feedback.data;
      } else {
        return []
      }
    }
  }

  async generateSummary(call_id: string) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      if (userData) {
        let response = await axios.post(`${this.url}/dashboard/generate-summary`, { call_id }, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } else {
        return null
      }
    }
  }

  async putSettings(data: any) {
    if (await this.authService.isLoggedIn()) {
      let userData = await this.authService.getUserData()
      //console.log("userData: ",userData)
      if (userData) {
        //console.log("userData.getIdToken(): ", await userData.getIdToken());
        let assistant = await axios.put(`${this.url}/dashboard/playbook`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });

        return assistant.data;
      } else {
        return []
      }
    }
  }


  copilot(data: any) {
    return new Promise(async (resolve, reject) => {
      if (await this.authService.isLoggedIn()) {
        let userData = await this.authService.getUserData();
        //console.log("userData: ",userData)
        if (userData) {
          //console.log("userData.getIdToken(): ", await userData.getIdToken());
          try {
            fetch(`${this.url}/dashboard/copilot`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(data)
            })
              .then(answer => {
                //console.log("answer.data: ", answer.data);
                resolve(answer);
              })
              .catch(error => {
                reject(error);
              });
          } catch (error) {
            reject(error);
          }
        } else {
          resolve([]);
        }
      } else {
        resolve([]);
      }
    })
  }


  async askToAI(data: any) {
    return new Promise(async (resolve, reject) => {
      if (await this.authService.isLoggedIn()) {
        let userData = await this.authService.getUserData();
        //console.log("userData: ",userData)
        if (userData) {
          //console.log("userData.getIdToken(): ", await userData.getIdToken());
          try {
            fetch(`${this.url}/dashboard/ask`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(data)
            })
              .then(answer => {
                //console.log("answer.data: ", answer.data);
                resolve(answer);
              })
              .catch(error => {
                reject(error);
              });
          } catch (error) {
            reject(error);
          }
        } else {
          resolve([]);
        }
      } else {
        resolve([]);
      }
    });



  }

  async postTicket(data: Ticket, files: File[] = []) {
    let userData = await this.authService.getUserData()
    if (userData) {

      if (!files.length) {
        let ticket = await axios.post(`${this.url}/dashboard/ticket`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return ticket.data;
      }

      // Con adjuntos va como multipart: sin fijar Content-Type, axios pone el
      // boundary solo. El campo "files" coincide con el multer global del
      // backend (upload.array("files", 10)).
      const form = new FormData();
      Object.entries(data as Record<string, any>).forEach(([key, value]) => {
        if (value !== undefined && value !== null) form.append(key, String(value));
      });
      files.forEach((file) => form.append('files', file));

      let ticket = await axios.post(`${this.url}/dashboard/ticket`, form, {
        headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` }
      });
      return ticket.data;
    } else {
      return []
    }
  }


  async clonePlaybook(data: any) {
    let userData = await this.authService.getUserData();
    if (userData) {
        try {
            const response = await axios.post(
                `${this.url}/dashboard/playbook/clone`,
                data,
                {
                    headers: {
                        'Authorization': `Bearer ${await userData.getIdToken()}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error cloning playbook:', error);
            throw error;
        }
    } else {
        throw new Error('User not authenticated');
    }
  }

  async getPlaybookToolsPreview(playbookId: string): Promise<{ success: boolean; tools: { name: string; description: string }[]; warning?: string }> {
    let userData = await this.authService.getUserData();
    if (userData) {
        try {
            const response = await axios.get(
                `${this.url}/dashboard/playbook/${playbookId}/tools`,
                {
                    headers: {
                        'Authorization': `Bearer ${await userData.getIdToken()}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error loading playbook tools preview:', error);
            throw error;
        }
    } else {
        throw new Error('User not authenticated');
    }
  }

  async getAvailablePhonesForCompany(companyId: string, assistantId?: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
        try {
            const response = await axios.get(
                `${this.url}/dashboard/phones/available-for-company`,
                {
                    params: assistantId
                        ? { company_id: companyId, assistant_id: assistantId }
                        : { company_id: companyId },
                    headers: {
                        'Authorization': `Bearer ${await userData.getIdToken()}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error getting available phones for company:', error);
            throw error;
        }
    } else {
        throw new Error('User not authenticated');
    }
  }

  async putUpdateTicket(data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      console.log("DATA: ", data)
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let ticket = await axios.put(`${this.url}/dashboard/ticket`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return ticket.data;
    } else {
      return []
    }
  }

  async updateCallTag(callId: string, tag: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        const response = await axios.put(`${this.url}/dashboard/call/tag`,
          { callId, tag },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error updating call tag:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }


  async createPlaybook(playbookData: any) {

    let userData = await this.authService.getUserData()
    if (userData) {
      console.log("DATA: ", playbookData)
      //console.log("userData.getIdToken(): ", await userData.getIdToken());
      let ticket = await axios.post(`${this.url}/dashboard/playbook`, playbookData, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return ticket.data;
    } else {
      return []
    }
  }

  async generateAIPrompt(instructions: string): Promise<string> {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/generate-ai-prompt`,
          { instructions },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data.prompt;
      } catch (error) {
        console.error('Error generating AI prompt:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async generateAIVoiceDesignPrompt(instructions: string): Promise<string> {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/generate-ai-voice-design-prompt`,
          { instructions },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data.prompt;
      } catch (error) {
        console.error('Error generating voice design prompt:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async generateAIVoiceSamplePhrasePrompt(instructions: string): Promise<string> {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        // Temporalmente usando el endpoint de diseño de voz hasta que se implemente el endpoint específico
        const response = await axios.post(`${this.url}/dashboard/generate-ai-voice-sample-phrase-prompt`,
          { instructions },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data.prompt;
      } catch (error) {
        console.error('Error generating voice sample phrase prompt:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getVoices(): Promise<any[]> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/voices`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data || [];
      } catch (error) {
        console.error('Error fetching voices:', error);
        return [];
      }
    }
    return [];
  }

  async getAvailableLLMModels(): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/simulator/available-models`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching LLM models:', error);
        return { success: false, models: [], grouped: {}, recommended: [] };
      }
    }
    return { success: false, models: [], grouped: {}, recommended: [] };
  }

  /**
   * Diseña 3 variantes de voz con eleven_ttv_v3.
   * `options` acepta autoGenerateText, shouldEnhance, loudness, guidanceScale,
   * seed y remixingSessionId ("quality" no existe en v3, "promptStrength" solo
   * es válido junto con reference_audio_base64, aún sin exponer en el front).
   */
  async designVoices(prompt: string, sampleText: string, options: any = {}): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(
          `${this.url}/dashboard/design-voices`,
          { prompt, sampleText, ...options },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            },
            timeout: 190000
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error designing voices:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /** Remezcla un preview sin guardar (generatedVoiceId) o una voz propia (voiceId interno). */
  async remixVoice(payload: {
    prompt: string;
    sampleText?: string;
    generatedVoiceId?: string;
    voiceId?: string;
    [key: string]: any;
  }): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(
          `${this.url}/dashboard/remix-voice`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            },
            timeout: 190000
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error remixing voice:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /** Catálogo de voces en español de España para añadir. gender/age: valor o 'any'. */
  async getVoiceLibrary(gender: string, age: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/voice-library`, {
        params: { gender, age },
        headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` },
        timeout: 60000
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  /** Muestra de audio (mp3) de una voz del catálogo. */
  async getVoiceLibraryPreview(voiceId: string): Promise<ArrayBuffer> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/voice-library/${encodeURIComponent(voiceId)}/preview`, {
        headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` },
        responseType: 'arraybuffer',
        timeout: 30000
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  /** Conecta una voz del catálogo: queda guardada en Voices y lista para los agentes. */
  async addLibraryVoice(voiceId: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.post(
        `${this.url}/dashboard/voice-library/add`,
        { voice_id: voiceId },
        {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          timeout: 60000
        }
      );
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  /** Edita metadatos de una voz propia. */
  async updateVoice(internalId: string, changes: any): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.put(
          `${this.url}/dashboard/voices/${internalId}`,
          changes,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error updating voice:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async saveVoice(voiceData: any): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(
          `${this.url}/dashboard/save-voice`,
          voiceData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error saving voice:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async simulateConversations(payload: any): Promise<{ jobId: string }> {
    let userData = await this.authService.getUserData();
    if (userData) {
        console.log("Sending payload:", payload); // Log the payload for debugging
        const response = await axios.post(`${this.url}/dashboard/simulate-conversations`, payload, {
            headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } else {
        throw new Error('User not authenticated');
    }
}

  async getSimulationStatus(jobId: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/simulate-conversations/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async generateAudio(
    text: string, 
    voiceId: string, 
    model?: string, 
    speed?: number, 
    stability?: number, 
    similarity_boost?: number
  ): Promise<ArrayBuffer> {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        const requestBody: any = { text, voiceId };
        
        // Add optional parameters only if they are provided
        if (model) requestBody.model = model;
        if (speed !== undefined) requestBody.speed = speed;
        if (stability !== undefined) requestBody.stability = stability;
        if (similarity_boost !== undefined) requestBody.similarity_boost = similarity_boost;
        
        const response = await axios.post(
          `${this.url}/dashboard/generate-audio`,
          requestBody,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer',
            // eleven_v3 es bastante más lento que flash: sin margen se cortaba.
            timeout: 130000
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error generating audio:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }
  async speechToText(audioData: string, mimeType: string, language: string = 'en'): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await fetch(`${this.url}/dashboard/advanced-editor/speech-to-text`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            audioData,
            mimeType,
            language
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error in speech to text:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async streamCopilotText(
    prompt: string, 
    context?: string, 
    fullContent?: string, 
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
    signal?: AbortSignal,
    attachments?: Array<{ name: string; type: 'text' | 'image'; content: string; mimeType: string; sourceFormat: string }>
  ): Promise<ReadableStream<Uint8Array>> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const body: any = {
          prompt,
          context,
          full_content: fullContent,
          conversation_history: conversationHistory || []
        };
        if (attachments?.length) {
          body.attachments = attachments;
        }
        const response = await fetch(`${this.url}/dashboard/advanced-editor/ai/copilot-stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body),
          signal: signal
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is empty');
        }

        // Return the raw response body stream directly
        // The component will handle SSE parsing to access structured events
        return response.body;
      } catch (error) {
        console.error('Error calling copilot stream:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async streamAIText(prompt: string, context?: string, fullContent?: string, widgetType: string = 'prompt-editor'): Promise<ReadableStream<Uint8Array>> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await fetch(`${this.url}/dashboard/advanced-editor/ai/stream-text`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt,
            context,
            full_content: fullContent,
            widgetType
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body available for streaming');
        }

        // Transform SSE format to plain text stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        return new ReadableStream({
          async start(controller) {
            let buffer = '';
            
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                controller.close();
                break;
              }
              
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') {
                    controller.close();
                    return;
                  }
                  try {
                    const parsed = JSON.parse(data);
                    if (parsed.text) {
                      controller.enqueue(new TextEncoder().encode(parsed.text));
                    }
                  } catch (e) {
                    // Ignore parse errors
                  }
                }
              }
            }
          }
        });
      } catch (error) {
        console.error('Error streaming AI text:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async generateAIText(prompt: string, context?: string, fullContent?: string, widgetType: string = 'general'): Promise<string> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/advanced-editor/ai/generate-text`, {
          prompt,
          context,
          full_content: fullContent,
          widgetType
        }, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          return response.data.text;
        } else {
          throw new Error(response.data.message || 'Error generating AI text');
        }
      } catch (error) {
        console.error('Error generating AI text:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async generateAIFunctionCall(instructions: string): Promise<string> {
    let userData = await this.authService.getUserData()
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/generate-ai-function-call`,
          { instructions },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data.prompt;
      } catch (error) {
        console.error('Error generating AI function call:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async requestActivation() {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.post(`${this.url}/dashboard/request-activation`, {}, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async calculateMessagesCost(params: CalculateCostsParams): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const queryParams = new URLSearchParams({
        minDuration: params.minDuration.toString(),
        maxDuration: params.maxDuration.toString(),
        prices: JSON.stringify(params.prices),
        callType: params.callType
      });

      if (params.playbookId) {
        queryParams.append('playbookId', params.playbookId);
      }
      if (params.callId) {
        queryParams.append('callId', params.callId);
      }

      const response = await axios.get(`${this.url}/dashboard/calculate-costs?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getTwilioSubaccounts() {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const token = await userData.getIdToken();
        const response = await axios.get(`${this.url}/dashboard/twilio-subaccounts`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (err: any) {
        console.error('Error fetching Twilio subaccounts:', err);
        if (err.response) {
            throw new Error(err.response.data.message || 'Server error');
        }
        throw new Error(err.message || 'Unknown error occurred');
    }
  }

  async getPhoneNumbersWithDetails(subaccount_id: string = 'main') {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const token = await userData.getIdToken();
        console.log(`Making request to /dashboard/phones for subaccount: ${subaccount_id}`);

        const response = await axios.get(`${this.url}/dashboard/phones`, {
            params: {
                subaccount_id: subaccount_id
            },
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        // Handle new response format with metadata
        if (response.data.metadata) {
            // New format with metadata
            const { numbers, metadata } = response.data;
            
            // Check if we got numbers (even with warning about credentials)
            if (Array.isArray(numbers)) {
                const processedPhones = numbers.map((phone: any) => {
                    let assistantId = null;
                    let playbookName = null;
                    let companyName = phone.company || '';

                    if (phone.webhook) {
                        try {
                            const url = new URL(phone.webhook);
                            assistantId = url.searchParams.get('assistant_id');
                        } catch (e) {
                            console.warn('Invalid webhook URL:', phone.webhook);
                        }
                    }

                    if (phone.friendly_name) {
                        const parts = phone.friendly_name.split(':');
                        if (parts.length > 1) {
                            companyName = parts[0].trim();
                            playbookName = parts[1].trim();
                        } else {
                            companyName = phone.friendly_name.trim();
                        }
                    }

                    const processedPhone = {
                        ...phone,
                        company: companyName,
                        playbooks_for_incoming_calls: assistantId ? [{
                            id: assistantId,
                            name: playbookName || 'Unknown Playbook'
                        }] : [],
                        playbooks_for_outgoing_calls: phone.playbooks_for_outgoing_calls || []
                    };

                    return processedPhone;
                });

                return {
                    phoneNumbers: processedPhones,
                    error: null,
                    errorMessage: null,
                    infoMessage: metadata.message // Info message about using main credentials
                };
            }
        }

        // Fallback to old format (for backward compatibility)
        if (Array.isArray(response.data)) {
            const processedPhones = response.data.map((phone: any) => {
                let assistantId = null;
                let playbookName = null;
                let companyName = phone.company || '';

                if (phone.webhook) {
                    try {
                        const url = new URL(phone.webhook);
                        assistantId = url.searchParams.get('assistant_id');
                    } catch (e) {
                        console.warn('Invalid webhook URL:', phone.webhook);
                    }
                }

                if (phone.friendly_name) {
                    const parts = phone.friendly_name.split(':');
                    if (parts.length > 1) {
                        companyName = parts[0].trim();
                        playbookName = parts[1].trim();
                    } else {
                        companyName = phone.friendly_name.trim();
                    }
                }

                const processedPhone = {
                    ...phone,
                    company: companyName,
                    playbooks_for_incoming_calls: assistantId ? [{
                        id: assistantId,
                        name: playbookName || 'Unknown Playbook'
                    }] : [],
                    playbooks_for_outgoing_calls: phone.playbooks_for_outgoing_calls || []
                };

                return processedPhone;
            });

            return {
                phoneNumbers: processedPhones,
                error: null,
                errorMessage: null
            };
        }

        return {
            phoneNumbers: response.data,
            error: null,
            errorMessage: null
        };
    } catch (err: any) {
        console.error('Error details:', err);
        if (err.response) {
            console.error('Response error:', err.response.data);
            throw new Error(err.response.data.message || 'Server error');
        }
        throw new Error(err.message || 'Unknown error occurred');
    }
  }

  async deletePhoneNumber(sid: string) {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const response = await axios.delete(`${this.url}/dashboard/phones/${sid}`, {
            headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error deleting phone number');
        }

        return response.data;
    } catch (err: any) {
        console.error('Error in deletePhoneNumber:', err);
        throw new Error(err.response?.data?.message || err.message || 'Error deleting phone number');
    }
  }

  async updatePhoneNumberWebhook(sid: string, webhook: string) {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const response = await axios.put(
            `${this.url}/dashboard/phones/${sid}/webhook`,
            { webhook },
            {
                headers: {
                    'Authorization': `Bearer ${await userData.getIdToken()}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error updating webhook');
        }

        return response.data;
    } catch (err: any) {
        console.error('Error in updatePhoneNumberWebhook:', err);
        throw new Error(err.response?.data?.message || err.message || 'Error updating webhook');
    }
  }

  // Get available phone numbers (not assigned to any agent)
  async getAvailablePhones(twilioAccountId?: string) {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const params: any = {};
        if (twilioAccountId) {
            params.twilio_account_id = twilioAccountId;
        }

        const response = await axios.get(`${this.url}/dashboard/phones/available`, {
            params: params,
            headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error fetching available phone numbers');
        }

        return {
            numbers: response.data.numbers || [],
            account_label: response.data.account_label || 'Main Account',
            account_id: response.data.account_id || 'main'
        };
    } catch (err: any) {
        console.error('Error in getAvailablePhones:', err);
        throw new Error(err.response?.data?.message || err.message || 'Error fetching available phone numbers');
    }
  }

  // Get available Twilio accounts (for admin to select)
  async getTwilioAccounts() {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const response = await axios.get(`${this.url}/dashboard/twilio-accounts`, {
            headers: {
                'Authorization': `Bearer ${await userData.getIdToken()}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error fetching Twilio accounts');
        }

        return response.data.accounts || [];
    } catch (err: any) {
        console.error('Error in getTwilioAccounts:', err);
        // If not admin or error, return empty array
        return [];
    }
  }

  async resetPhoneNumber(sid: string) {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const response = await axios.post(
            `${this.url}/dashboard/phones/${sid}/reset`,
            {},
            {
                headers: {
                    'Authorization': `Bearer ${await userData.getIdToken()}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error resetting phone number');
        }

        return response.data;
    } catch (err: any) {
        console.error('Error in resetPhoneNumber:', err);
        throw new Error(err.response?.data?.message || err.message || 'Error resetting phone number');
    }
  }

  async assignPhoneToAgent(sid: string, assistant_id: string, subaccount_id?: string, phone_usage: 'both' | 'inbound' | 'outbound' = 'both') {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const response = await axios.post(
            `${this.url}/dashboard/phones/${sid}/assign`,
            { assistant_id, subaccount_id, phone_usage },
            {
                headers: {
                    'Authorization': `Bearer ${await userData.getIdToken()}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error assigning phone to agent');
        }

        return response.data;
    } catch (err: any) {
        console.error('Error in assignPhoneToAgent:', err);
        throw new Error(err.response?.data?.message || err.message || 'Error assigning phone to agent');
    }
  }

  async returnPhoneToTwilio(sid: string) {
    try {
        let userData = await this.authService.getUserData();
        if (!userData) {
            throw new Error('User not authenticated');
        }

        const response = await axios.post(
            `${this.url}/dashboard/phones/${sid}/return`,
            {},
            {
                headers: {
                    'Authorization': `Bearer ${await userData.getIdToken()}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.data.success) {
            throw new Error(response.data.message || 'Error returning phone to Twilio');
        }

        return response.data;
    } catch (err: any) {
        console.error('Error in returnPhoneToTwilio:', err);
        throw new Error(err.response?.data?.message || err.message || 'Error returning phone to Twilio');
    }
  }

  async getCallStats(params: {
    startDate: string,
    endDate: string,
    type: string,
    min_duration?: number | null,
    mode?: string,
    viewBy?: string,
    conversionOutputParam?: string | null,
    conversionOutputParamType?: string | null,
    conversionOutputParamValues?: string | null,
    conversionOutputParamLogic?: string | null
  }) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const queryParams: any = {
        startDate: params.startDate,
        endDate: params.endDate,
        type: params.type,
        mode: params.mode || 'total',
        min_duration: params.min_duration,
        viewBy: params.viewBy || 'calls'
      };
      if (params.conversionOutputParam) {
        queryParams.conversionOutputParam = params.conversionOutputParam;
        queryParams.conversionOutputParamType = params.conversionOutputParamType || 'boolean';
      }
      if (params.conversionOutputParamValues) {
        queryParams.conversionOutputParamValues = params.conversionOutputParamValues;
        queryParams.conversionOutputParamLogic = params.conversionOutputParamLogic || 'or';
      }
      let stats = await axios.get(`${this.url}/dashboard/calls/stats`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: queryParams
      });
      return stats.data;
    } else {
      return []
    }
  }

  async getOutputParamValues(paramName: string, assistantIds: string[]): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      const params = new URLSearchParams();
      params.set('paramName', paramName);
      if (assistantIds.length > 0) params.set('assistantIds', assistantIds.join(','));
      const response = await axios.get(`${this.url}/dashboard/calls/output-param-values?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`
        }
      });
      return response.data;
    }
    throw new Error('User not authenticated');
  }

  async getCallInsights(data: { stats: any[], agents: string[], dateRange: { start: string, end: string }, viewMode: string }): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.post(`${this.url}/dashboard/calls/insights`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    }
    throw new Error('User not authenticated');
  }

  async getAgentMetrics(agentId: string, params?: {
    startDate?: string,
    endDate?: string
  }) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let metrics = await axios.get(`${this.url}/dashboard/agent/${agentId}/metrics`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: params,
        timeout: 20000,
      });
      return metrics.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  // Histórico de latencia batcheado para varios agentes a la vez (usado por
  // el sparkline de cada fila en /agents-v2) — una sola petición para N
  // agentes en vez de N peticiones a getAgentMetrics().
  async getAgentsLatencyHistory(ids: string[], days: number = 14): Promise<Record<string, Array<{ date: string; avg_latency_ms: number | null; calls: number }>>> {
    if (!ids || ids.length === 0) return {};
    let userData = await this.authService.getUserData()
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/agents/latency-history`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: { ids: ids.join(','), days }
      });
      return response.data || {};
    } else {
      throw new Error('User not authenticated');
    }
  }

  async initBulkCalls(
    assistant_id: string, 
    call: 'start' | 'stop', 
    contactIds?: string[], 
    segmentId?: string,
    is_low_latency: boolean = false,
    campaignStartDate?: string,
    retryCallTimes?: {date: string, time: string}[]
  ): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        let urlInit = this.url+'/dashboard';

        // Check if the playbook is low latency
        /*if (is_low_latency === true) {
          console.log(`Using low latency URL for playbook ${assistant_id}`);
          if (this.url.includes('localhost')) {
            // En desarrollo local, usar la misma URL base en lugar del puerto 8000
            console.log('Low latency mode disabled in local development');
            urlInit = this.url + '/dashboard';
          } else {
            urlInit = 'https://lowlatency.meetzy.io';
          }
        }*/

        const response = await axios.post(
          `${urlInit}/init-bulk`,
          { 
            assistant_id,
            call,
            contactIds,
            segmentId,
            campaignStartDate,
            retryCallTimes
          },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
        
      } catch (error) {
        console.error('Error in initBulkCalls:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  getRecordingUrl(callId: string): Promise<any> {
    return this.http.get<any>(`${this.url}/calls/${callId}/recording`).toPromise();
  }

  async addTag(callId: string, tag: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.put(
          `${this.url}/dashboard/call/tag`, 
          { 
            callId, 
            tag 
          },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error in addTag:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async removeTag(callId: string, tag: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.delete(
          `${this.url}/dashboard/call/tags/${callId}/${tag}`,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error in removeTag:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getZohoServices() {
    let userData = await this.authService.getUserData();
    if (userData) {
        try {
            const response = await axios.get(`${this.url}/dashboard/zoho/services`, {
                headers: {
                    'Authorization': `Bearer ${await userData.getIdToken()}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching Zoho services:', error);
            throw error;
        }
    } else {
        throw new Error('User not authenticated');
    }
  }

  async updateZohoServiceId(serviceId: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.put(
          `${this.url}/dashboard/integrations/zoho/service`,
          { service_id: serviceId },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error updating Zoho service ID:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async updateGoogleAdsCustomerId(customerId: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.put(
          `${this.url}/dashboard/integrations/google-ads/customer-id`,
          { customer_id: customerId },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error updating Google Ads Customer ID:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getGoogleAdsCampaigns(params: any = {}): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/google-ads/campaigns`, {
          headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` },
          params
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching Google Ads campaigns:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getGoogleAdsCampaignStats(campaignId: string, params: any = {}): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/google-ads/campaign/${campaignId}/stats`, {
          headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` },
          params
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching Google Ads campaign stats:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getGoogleAdsCustomerInfo(): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/google-ads/customer-info`, {
          headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching Google Ads customer info:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async updatePhoneNumber(data: {
    sid: string;
    webhook: string | null;
    webhook_method: string;
    status_callback: string | null;
    status_callback_method: string;
    playbook_id: string | null;
    friendly_name: string;
  }) {
    const userData = await this.authService.getUserData();
    if (!userData) {
      throw new Error('Not authenticated');
    }

    const response = await axios.put(
      `${this.url}/dashboard/phones/${data.sid}`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  }

  async getCustomers(params?: { startDate?: string; endDate?: string }) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/customers`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: params
      });
      return response.data;
    } else {
      return [];
    }
  }

  // New methods for Google Sheets integration

  async getGoogleAuthUrl() {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/contacts/google-auth-url`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async submitGoogleAuthCode(code: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.post(`${this.url}/dashboard/contacts/google-auth-callback`, 
        { code },
        {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getSpreadsheets() {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/spreadsheets`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching spreadsheets:', error.response?.data || error.message);
        
        // Propagar el error completo para que el componente pueda manejarlo adecuadamente
        // Especialmente importante para errores de permisos (403)
        if (error.response && error.response.status === 403) {
          console.warn('Permission error detected in API service:', error.response.data);
        }
        
        throw error; // Propagar el error original con todos sus detalles
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getSpreadsheetColumns(spreadsheetId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/spreadsheet/${spreadsheetId}/columns`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching spreadsheet columns:', error.response?.data || error.message);
        
        // Check for permission errors
        if (error.response && error.response.status === 403) {
          console.warn('Permission error detected when fetching columns:', error.response.data);
          
          // Extract information about required permissions if available
          if (error.response.data && error.response.data.requiredScopes) {
            throw {
              ...error,
              needsReauth: true,
              requiredScopes: error.response.data.requiredScopes,
              message: 'Insufficient permissions to access spreadsheet columns'
            };
          }
          
          throw error;
        }
        
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getSheetData(spreadsheetId: string, sheetName: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/spreadsheet/${spreadsheetId}/sheet/${sheetName}`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching sheet data:', error.response?.data || error.message);
        
        // Check for permission errors
        if (error.response && error.response.status === 403) {
          console.warn('Permission error detected when fetching sheet data:', error.response.data);
          
          // Extract information about required permissions if available
          if (error.response.data && error.response.data.requiredScopes) {
            throw {
              ...error,
              needsReauth: true,
              requiredScopes: error.response.data.requiredScopes,
              message: 'Insufficient permissions to access sheet data'
            };
          }
          
          throw error;
        }
        
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async importContacts(data: {
    spreadsheetId: string;
    sheetName: string;
    columnMapping: Record<string, number>;
    phoneColumnIndex: number;
    customFieldsMapping?: Array<{columnIndex: number, key: string}>;
  }) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.post(`${this.url}/dashboard/contacts/import`, 
        data,
        {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async processExcelFile(formData: FormData) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        console.log('Calling processExcelFile API...');
        console.log('URL:', `${this.url}/dashboard/contacts/process-excel`);
        
        const response = await axios.post(`${this.url}/dashboard/contacts/process-excel`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              // Don't set Content-Type for FormData, let axios set it with boundary
            },
            withCredentials: true
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error processing Excel file:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getExcelSheetData(file: File, sheetName: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        console.log('Getting Excel sheet data for sheet:', sheetName);
        
        const formData = new FormData();
        formData.append('files', file);
        formData.append('sheetName', sheetName);
        
        const response = await axios.post(`${this.url}/dashboard/contacts/excel-sheet-data`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              // Don't set Content-Type for FormData, let axios set it with boundary
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error getting Excel sheet data:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async testCrmEndpoints() {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        console.log('Testing CRM endpoints...');
        const response = await axios.get(`${this.url}/dashboard/contacts/test`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error testing CRM endpoints:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // Removed testExcelEndpoint - use processExcelFile instead

  // ============================================================================
  // NEW IMPORT SYSTEM V2
  // ============================================================================

  /**
   * Validates an Excel file and returns preview data
   */
  async validateImportFile(formData: FormData) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/contacts/v2/validate`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error validating import file:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Gets data for a specific sheet in an Excel file
   */
  async getImportSheetData(formData: FormData) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/contacts/v2/sheet-data`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error getting sheet data:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Starts the import process and returns a job ID
   */
  async startImportV2(formData: FormData): Promise<{ jobId: string; totalRows: number }> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/contacts/v2/import`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error starting import:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Gets the current status of an import job
   */
  async getImportStatus(jobId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/v2/import-status/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error getting import status:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Gets the full report for a completed import job
   */
  async getImportReport(jobId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/v2/import-report/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error getting import report:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // ============================================================================
  // GOOGLE SHEETS IMPORT V2
  // ============================================================================

  /**
   * Check if Google Sheets integration is connected and working (v2)
   */
  async checkGoogleSheetsConnection(): Promise<{ connected: boolean; message: string }> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/v2/google-sheets/check-integration`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error checking Google Sheets connection:', error.response?.data || error.message);
        return { connected: false, message: 'Error checking connection' };
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * List available Google Spreadsheets
   */
  async listGoogleSpreadsheets(): Promise<{ spreadsheets: any[]; total: number }> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/v2/google-sheets/list`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error listing Google Spreadsheets:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Validate a Google Spreadsheet and get preview data
   */
  async validateGoogleSpreadsheet(spreadsheetId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/contacts/v2/google-sheets/validate/${spreadsheetId}`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error validating Google Spreadsheet:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Get data for a specific sheet in a Google Spreadsheet
   */
  async getGoogleSheetData(spreadsheetId: string, sheetName: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(
          `${this.url}/dashboard/contacts/v2/google-sheets/sheet-data/${spreadsheetId}/${encodeURIComponent(sheetName)}`, 
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error getting Google Sheet data:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Start import from Google Spreadsheet
   */
  async startGoogleSheetsImport(payload: {
    spreadsheetId: string;
    spreadsheetName?: string;
    sheetName: string;
    columnMapping: Record<string, number>;
    phoneColumnIndex: number;
    customFieldsMapping?: any[];
    segmentName?: string;
    companyId?: string;
  }): Promise<{ jobId: string; totalRows: number }> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/contacts/v2/google-sheets/import`, payload, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error starting Google Sheets import:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // ============================================================================
  // LEGACY IMPORT (kept for backwards compatibility)
  // ============================================================================

  async importContactsFromExcel(formData: FormData) {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/contacts/import-excel`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              // Don't set Content-Type for FormData, let axios set it with boundary
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error importing contacts from Excel:', error.response?.data || error.message);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getContacts(params: { page?: number; limit?: number; search?: string; startDate?: string; endDate?: string; companyId?: string } = {}) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contacts`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params
      });
      
      return response.data;
    } else {
      return { contacts: [], pagination: { total: 0 } };
    }
  }

  // WhatsApp Templates methods
  async getWhatsAppTemplates() {
    const userData = await this.authService.getUserData();
    if (!userData) return { templates: [] };
    const url = `${this.url}/dashboard/whatsapp/templates`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${await userData.getIdToken()}` },
    });
    return response.data;
  }

  async createWhatsAppTemplate(data: {
    name: string;
    category: string;
    language: string;
    body_text: string;
    footer_text?: string;
    header_text?: string;
    buttons?: { text: string }[];
  }) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const url = `${this.url}/dashboard/whatsapp/templates`;
    const response = await axios.post(url, data, {
      headers: { Authorization: `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  async updateWhatsAppTemplate(id: string, data: {
    body_text: string;
    footer_text?: string;
    header_text?: string;
    buttons?: { text: string }[];
  }) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const url = `${this.url}/dashboard/whatsapp/templates/${id}`;
    const response = await axios.put(url, data, {
      headers: { Authorization: `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  async deleteWhatsAppTemplate(name: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const url = `${this.url}/dashboard/whatsapp/templates/${name}`;
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${await userData.getIdToken()}` },
    });
    return response.data;
  }

  async getMetaWabaId(userId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const url = `${this.url}/dashboard/users/${userId}/meta-waba-id`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${await userData.getIdToken()}` },
    });
    return response.data;
  }

  async setMetaWabaId(userId: string, metaWabaId: string) {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('Not authenticated');
    const url = `${this.url}/dashboard/users/${userId}/meta-waba-id`;
    const response = await axios.patch(url, { meta_waba_id: metaWabaId }, {
      headers: { Authorization: `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  // Contact Lists methods
  async getContactSegments(params: { companyId?: string } = {}) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params
      });
      
      return response.data;
    } else {
      return { segments: [] };
    }
  }

  // Get segments for a specific contact
  async getSegmentsForContact(contactId: string): Promise<{ success: boolean; segments: any[] }> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contacts/${contactId}/segments`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false, segments: [] };
    }
  }

  async createContactSegment(data: { name: string; description?: string; companyId?: string }) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments`;
      
      const response = await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false };
    }
  }

  async updateContactSegment(segmentId: string, data: { name: string; description?: string }) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments/${segmentId}`;
      
      const response = await axios.put(url, data, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false };
    }
  }

  async updateContact(contactId: string, data: Record<string, any>) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contacts/${contactId}`;
      
      const response = await axios.put(url, data, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false };
    }
  }

  async bulkDeleteContacts(contactIds: string[]): Promise<{ success: boolean; deletedCount?: number; message?: string }> {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contacts/bulk`;
      
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        data: { contactIds }
      });
      
      return response.data;
    } else {
      return { success: false, message: 'Not authenticated' };
    }
  }

  async deleteContactSegment(segmentId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments/${segmentId}`;
      
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false };
    }
  }

  async getContactsInSegment(segmentId: string, params: { page?: number; limit?: number; companyId?: string } = {}) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments/${segmentId}/contacts`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params
      });
      
      return response.data;
    } else {
      return { contacts: [], segment: null, pagination: { total: 0 } };
    }
  }

  async searchContacts(prompt: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contacts/search`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        },
        params: { prompt }
      });
      
      return response.data;
    } else {
      return { contacts: [] };
    }
  }

  async addContactsToSegment(segmentId: string, contactIds: string[], companyId?: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments/${segmentId}/contacts`;
      
      const payload: any = { contactIds };
      if (companyId) {
        payload.companyId = companyId;
      }
      
      const response = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false };
    }
  }

  async removeContactFromSegment(segmentId: string, contactId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      const url = `${this.url}/dashboard/contact-segments/${segmentId}/contacts/${contactId}`;
      
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } else {
      return { success: false };
    }
  }

  async submitFeedback(data: { message: string, rating?: number, category?: string }) {
    let userData = await this.authService.getUserData()
    if (userData) {
      let response = await axios.post(`${this.url}/api/feedback`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      return { success: false, message: 'No user data available' };
    }
  }

  async getChangelog(): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(`${this.url}/dashboard/changelog`, { headers });
    return response.data;
  }

  async getLatestVersion(): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(`${this.url}/dashboard/changelog/latest`, { headers });
    return response.data;
  }

  // Preferencia "encuesta de producto vista/descartada", persistida en backend
  // (no localStorage) para que no reaparezca en otro dispositivo del mismo usuario.
  async getSurveyDismissed(): Promise<boolean> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(`${this.url}/dashboard/table-preferences/external_survey_2026`, { headers });
    return !!response.data?.config?.dismissed;
  }

  async setSurveyDismissed(): Promise<void> {
    const headers = await this.getAuthHeaders();
    await axios.put(
      `${this.url}/dashboard/table-preferences/external_survey_2026`,
      { dismissed: true, dismissed_at: new Date().toISOString() },
      { headers }
    );
  }

  // Nuevo método para verificar si existe una integración de Google Sheets
  async checkGoogleSheetsIntegration() {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/integrations/check`, {
          params: { service: 'google-sheets' },
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error checking Google Sheets integration:', error.response?.data || error.message);
        
        if (error.response && error.response.status === 404) {
          return { exists: false };
        }
        
        // Check for permission errors
        if (error.response && error.response.status === 403) {
          console.warn('Permission error detected in integration check:', error.response.data);
          // Include information about required permissions if available
          if (error.response.data && error.response.data.requiredScopes) {
            return { 
              exists: false, 
              error: true, 
              needsReauth: true,
              requiredScopes: error.response.data.requiredScopes,
              message: 'Insufficient permissions for Google Sheets integration'
            };
          }
          
          return { 
            exists: false, 
            error: true, 
            needsReauth: true,
            message: 'Insufficient permissions for Google Sheets integration'
          };
        }
        
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // Get campaigns
  async getCampaigns(): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(
          `${this.url}/dashboard/campaigns`,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error in getCampaigns:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // Cancel a campaign
  async cancelCampaign(campaignId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.delete(
      `${this.url}/dashboard/campaigns/${campaignId}`,
      {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  }

  // Update campaign (reschedule or change status)
  async updateCampaign(campaignId: string, updates: { newLaunchTime?: string; status?: string }): Promise<any> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.put(
      `${this.url}/dashboard/campaigns/${campaignId}`,
      updates,
      {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  }


  // Get bulk calls status
  async getBulkCallsStatus(): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(
          `${this.url}/dashboard/bulk-status`,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Si la respuesta no tiene el formato esperado, transformarla
        if (response.data && response.data.success === true && response.data.active === true) {
          // Formato antiguo con una sola llamada activa
          return {
            success: true,
            activeCalls: [{
              assistantId: response.data.assistantId,
              totalContacts: response.data.totalContacts,
              remainingContacts: response.data.remainingContacts,
              startTime: response.data.startTime
            }]
          };
        }
        
        return response.data;
      } catch (error) {
        console.error('Error getting bulk calls status:', error);
        return { success: false, error: 'Failed to get bulk calls status' };
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async get(endpoint: string) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const response = await axios.get(`${this.url}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    }
    return null;
  }

  async post(endpoint: string, data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const response = await axios.post(`${this.url}${endpoint}`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    }
    return null;
  }

  // ==================== PHONE HEALTH METHODS ====================
  
  /**
   * Get phone health information for an agent
   */
  async getPhoneHealth(agentId: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const token = await userData.getIdToken();
        
        
        const response = await axios.get(`https://lowlatency.meetzy.es/agent/${agentId}/phone-health`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          // Ticket #415: sin timeout, una consulta colgada en el backend dejaba la
          // petición pendiente indefinidamente hasta que el navegador o el ALB la
          // cortaban, mostrando un "Network Error" genérico sin contexto.
          timeout: 20000
        });
        


        console.log('📱 Phone Health - Response received!:', {
          status: response.status,
          dataExists: !!response.data,
          dataPreview: response.data ? Object.keys(response.data) : 'NO DATA'
        });
        
        return response.data;
      } catch (error: any) {
        console.error('📱 Phone Health - Error details:', {
          agentId,
          error: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data
        });
        // Ticket #415: propagamos un mensaje accionable (el que devuelve el backend,
        // o uno específico para timeout/red) en vez del "Network Error" genérico de
        // axios, que el modal mostraba tal cual sin que el usuario supiera qué pasó.
        const backendMessage = error.response?.data?.error;
        if (backendMessage) {
          error.message = backendMessage;
        } else if (error.code === 'ECONNABORTED') {
          error.message = 'The request timed out while checking phone number health. Please try again.';
        } else if (!error.response) {
          error.message = 'Could not reach the phone health service. Check your connection and try again.';
        }
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Return a phone number to Twilio (releases it permanently)
   */
  async returnPhoneNumber(sid: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const token = await userData.getIdToken();
        
        const response = await axios.post(`${this.url}/dashboard/phones/${sid}/return`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📱 Phone Return - Response received!:', {
          status: response.status,
          message: response.data.message,
          phoneNumber: response.data.phoneNumber
        });
        
        return response.data;
      } catch (error: any) {
        console.error('📱 Phone Return - Error details:', {
          sid,
          error: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data
        });
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Disconnect a phone number from an agent (removes from 'from' field)
   */
  async disconnectPhoneNumber(phoneNumber: string, agentId: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const token = await userData.getIdToken();
        
        const response = await axios.post(`${this.url}/dashboard/phones/${phoneNumber}/disconnect`, {
          agentId: agentId
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📱 Phone Disconnect - Response received!:', {
          status: response.status,
          message: response.data.message,
          phoneNumber: response.data.phoneNumber,
          agentId: response.data.agentId
        });
        
        return response.data;
      } catch (error: any) {
        console.error('📱 Phone Disconnect - Error details:', {
          phoneNumber,
          agentId,
          error: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data
        });
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getAvailablePhoneNumbers(agentId?: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const token = await userData.getIdToken();
        
        // Build query params - pass agentId if provided to get numbers from the correct Twilio account
        const params = new URLSearchParams();
        if (agentId) {
          params.append('agent_id', agentId);
        }
        const queryString = params.toString();
        const url = `${this.url}/dashboard/phones/available${queryString ? '?' + queryString : ''}`;
        
        const response = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📱 Available Phone Numbers - Response received!:', {
          status: response.status,
          count: response.data.numbers?.length || 0,
          accountLabel: response.data.account_label
        });
        
        return response.data;
      } catch (error: any) {
        console.error('📱 Available Phone Numbers - Error details:', {
          agentId,
          error: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data
        });
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async swapPhoneNumbers(agentId: string, oldPhoneNumber: string): Promise<any> {
    let userData = await this.authService.getUserData();
    if (userData) {
      try {
        const token = await userData.getIdToken();
        
        const response = await axios.post(`${this.url}/dashboard/phones/swap`, {
          agentId: agentId,
          oldPhoneNumber: oldPhoneNumber
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📱 Phone Swap - Response received!:', {
          status: response.status,
          message: response.data.message,
          oldNumber: response.data.oldNumber,
          newNumber: response.data.newNumber,
          agentId: response.data.agentId
        });
        
        return response.data;
      } catch (error: any) {
        console.error('📱 Phone Swap - Error details:', {
          agentId,
          oldPhoneNumber,
          error: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data
        });
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  async put(endpoint: string, data: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      const response = await axios.put(`${this.url}${endpoint}`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    }
    return null;
  }

  async getCompanyMembers(companyId: string) {
    let userData = await this.authService.getUserData();
    if (userData) {
      let response = await axios.get(`${this.url}/dashboard/company-members?company_id=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      return [];
    }
  }

  async updateAgentCompany(data: { agent_id: string; company_id: string }) {
    let userData = await this.authService.getUserData();
    if (userData) {
      let response = await axios.put(`${this.url}/dashboard/update-agent-company`, data, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      return { success: false, message: 'User not authenticated' };
    }
  }


  /** EVALUATIONS */

  // Get evaluations for a specific call
  async getCallEvaluations(callId: string): Promise<any[]> {
    try {
      await this.refreshAuthToken();
      const token = await this.authService.getToken();
      
      const response = await this.http.get<any[]>(
        `${this.url}/dashboard/call_evaluations/${callId}`,
        {
          headers: { 'Authorization': token || '' }
        }
      ).toPromise();
      
      return response || [];
    } catch (error) {
      console.error('Error fetching call evaluations:', error);
      return [];
    }
  }

  // Get all evaluations for a playbook
  async getEvaluations(playbookId: string): Promise<Evaluation[]> {
    try {
      await this.refreshAuthToken();
      const response = await axios.get(
        `${this.url}/dashboard/evaluations/${playbookId}`,
        {
          headers: {
            Authorization: 'Bearer ' + this.authToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      throw error;
    }
  }

  // Create a new evaluation
  async createEvaluation(playbookId: string, evaluation: Evaluation): Promise<Evaluation> {
    try {
      // Asegurarse de que los parámetros son correctos para el tipo de evaluación
      const evalToSend = this.prepareEvaluationData(evaluation);
      
      const response = await axios.post(
        `${this.url}/dashboard/evaluations/${playbookId}`,
        evalToSend,
        {
          headers: {
            Authorization: 'Bearer ' + this.authToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating evaluation:', error);
      throw error;
    }
  }

  // Update an existing evaluation
  async updateEvaluation(evaluationId: string, evaluation: Evaluation): Promise<Evaluation> {
    try {
      // Asegurarse de que los parámetros son correctos para el tipo de evaluación
      const evalToSend = this.prepareEvaluationData(evaluation);
      
      const response = await axios.put(
        `${this.url}/dashboard/evaluations/${evaluationId}`,
        evalToSend,
        {
          headers: {
            Authorization: 'Bearer ' + this.authToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating evaluation:', error);
      throw error;
    }
  }

  // Delete an evaluation
  async deleteEvaluation(evaluationId: string): Promise<void> {
    try {
      await axios.delete(
        `${this.url}/dashboard/evaluations/${evaluationId}`,
        {
          headers: {
            Authorization: 'Bearer ' + this.authToken
          }
        }
      );
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      throw error;
    }
  }

  // Helper para preparar datos de evaluación para enviar al servidor
  private prepareEvaluationData(evaluation: Evaluation): any {
    const result = { ...evaluation };
    
    // All evaluations are boolean type
    if (typeof evaluation.type !== 'string') {
      result.type = 'boolean';
    }
    
    // Ensure parameters is a valid string
    if (!result.parameters) {
      result.parameters = '{}';
    } else if (typeof result.parameters !== 'string') {
      result.parameters = JSON.stringify(result.parameters);
    }
    
    // Remove UI-specific properties before sending to the server
    delete result.config;
    delete result.showDetails;
    
    return result;
  }

  /** EVALUATION TRENDS */

  async getEvaluationTrends(params: { period?: string; from?: string; to?: string; assistant_id?: string } = {}): Promise<any[]> {
    try {
      await this.refreshAuthToken();
      const queryParams = new URLSearchParams();
      if (params.period) queryParams.set('period', params.period);
      if (params.from) queryParams.set('from', params.from);
      if (params.to) queryParams.set('to', params.to);
      if (params.assistant_id) queryParams.set('assistant_id', params.assistant_id);

      const response = await axios.get(
        `${this.url}/dashboard/evaluation-trends?${queryParams.toString()}`,
        {
          headers: {
            Authorization: 'Bearer ' + this.authToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching evaluation trends:', error);
      return [];
    }
  }

  async getEvaluationTrendsSummary(params: { from?: string; to?: string; assistant_id?: string } = {}): Promise<any[]> {
    try {
      await this.refreshAuthToken();
      const queryParams = new URLSearchParams();
      if (params.from) queryParams.set('from', params.from);
      if (params.to) queryParams.set('to', params.to);
      if (params.assistant_id) queryParams.set('assistant_id', params.assistant_id);

      const response = await axios.get(
        `${this.url}/dashboard/evaluation-trends/summary?${queryParams.toString()}`,
        {
          headers: {
            Authorization: 'Bearer ' + this.authToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching evaluation trends summary:', error);
      return [];
    }
  }

  // Add new method for admin to create company members
  async createCompanyMember(memberData: any) {
    let userData = await this.authService.getUserData()
    if (userData) {
      // Convert assistants array to comma-separated string if needed
      if (Array.isArray(memberData.assistants)) {
        memberData.assistants = memberData.assistants.join(',');
      }

      const response = await axios.post(`${this.url}/dashboard/main/company-member`, memberData, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      return { error: 'User not authenticated' }
    }
  }

  // ==================== ASSISTANT METHODS ====================
  
  /**
   * Verificar si el usuario es admin
   */
  async getUserStatus(): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/generator/user-status`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error getting user status:', error);
        throw error;
      }
    }
    throw new Error('User not authenticated');
  }

  /**
   * Verificar si el usuario es admin (endpoint específico)
   */
  async isAdmin(): Promise<boolean> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/admin-status`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data.isAdmin || false;
      } catch (error: any) {
        console.error('Error checking admin status:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Get call links for Twilio and ElevenLabs (admin only)
   */
  async getCallLinks(callId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/admin/call-links/${callId}`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error getting call links:', error);
        throw error;
      }
    }
    throw new Error('User not authenticated');
  }

  /**
   * Crear nuevo Assistant
   */
  async createAssistant(assistantData: any, commitMessage: string, targetCompany?: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const requestData: any = {
          assistantData,
          commitMessage
        };

        // Si se especifica targetCompany, incluirla en la request
        if (targetCompany) {
          requestData.targetCompany = targetCompany;
        }

        const response = await axios.post(`${this.url}/dashboard/generator/create-assistant`, requestData, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        // Si es un error de axios con respuesta del servidor, propagar el mensaje específico
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        // Si no, propagar el error original
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Obtener Assistant por ID
   */
  async getAssistant(assistantId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/generator/assistant/${assistantId}`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  async getCalendars(assistantId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/calendars`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Email domain (Resend) management for the Send Email tool.
   */
  async getEmailDomain(assistantId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.get(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/email-domain`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  async linkEmailDomain(assistantId: string, domain: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.post(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/email-domain`, { domain }, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  async verifyEmailDomain(assistantId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.post(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/email-domain/verify`, {}, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  async unlinkEmailDomain(assistantId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.delete(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/email-domain`, {
      headers: {
        'Authorization': `Bearer ${await userData.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Commit cambios a Assistant existente
   */
  async commitAssistantChanges(data: {
    assistantId: string;
    commitMessage: string;
    changes: any[];
    assistantData?: any; // Opcional para modo edición
  }): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/generator/commit-assistant`, data, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        // Si es un error de axios con respuesta del servidor, propagar el mensaje específico
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        // Si no, propagar el error original
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Listar Assistants del usuario/company
   */
  async getAssistants(): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/generator/assistants`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Obtener historial de versiones de un Assistant
   */
  async getAssistantVersions(assistantId: string): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      const response = await axios.get(`${this.url}/dashboard/generator/assistant/${assistantId}/versions`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      throw new Error('User not authenticated');
    }
  }

  // ==================== ADVANCED EDITOR METHODS ====================

  /**
   * Obtener Voice Agents disponibles para el Advanced Editor
   */
  async getVoiceAgents(): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/advanced-editor/voice-agents`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching voice agents:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // ==================== CALL LOGS METHODS ====================

  /**
   * Obtener CallLogs con filtros y paginación
   */
  async getCallLogs(params: {
    assistant_id?: string;
    start_date?: string;
    end_date?: string;
    search_text?: string;
    case_sensitive?: boolean;
    page?: number;
    limit?: number;
    live?: boolean;
  } = {}): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        // Convertir parámetros booleanos a strings
        const queryParams: any = { ...params };
        if (typeof queryParams.case_sensitive === 'boolean') {
          queryParams.case_sensitive = queryParams.case_sensitive.toString();
        }
        if (typeof queryParams.live === 'boolean') {
          queryParams.live = queryParams.live.toString();
        }

        const response = await axios.get(`${this.url}/dashboard/call-logs`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          params: queryParams
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching call logs:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Obtener lista de asistentes disponibles para filtros
   */
  async getCallLogsAssistants(): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/call-logs/assistants`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching assistants for call logs:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Obtener estadísticas básicas de CallLogs
   */
  async getCallLogsStats(params: {
    start_date?: string;
    end_date?: string;
  } = {}): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/call-logs/stats`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          params
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching call logs stats:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Verificar si el usuario tiene acceso a CallLogs
   */
  async hasCallLogsAccess(): Promise<boolean> {
    try {
      await this.getCallLogsStats();
      return true;
    } catch (error: any) {
      if (error.response && error.response.status === 403) {
        return false;
      }
      // Si es otro tipo de error, también asumimos que no tiene acceso
      return false;
    }
  }

  // ==================== COPILOT TICKETS METHODS ====================

  /**
   * Obtener tickets relacionados con un assistant para análisis en copilot
   * @param status - Opcional: 'PENDING', 'IN_PROGRESS', 'IN_REVIEW', etc. Si no se especifica, excluye tickets cerrados
   */
  async getCopilotTickets(assistantId: string, status?: string, limit: number = 50): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/tickets`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          },
          params: {
            status,
            limit
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching copilot tickets:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Analizar tickets seleccionados con transcripciones y feedback
   */
  async analyzeCopilotTickets(assistantId: string, ticketIds: string[]): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/analyze-tickets`, {
          ticket_ids: ticketIds
        }, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error analyzing copilot tickets:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // ============================================
  // CALLER IDS (client numbers for outbound)
  // ============================================

  async getCallerIds(assistantId: string): Promise<{ success: boolean; caller_ids: any[] }> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.get(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/caller-ids`, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` }
    });
    return response.data;
  }

  async validateCallerId(assistantId: string, friendlyName: string, phoneNumber: string): Promise<{ success: boolean; validation_code: string; phone_number: string; friendly_name: string; caller_ids: any[] }> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.post(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/caller-ids/validate`, {
      friendly_name: friendlyName,
      phone_number: phoneNumber
    }, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  async updateCallerIdStatus(assistantId: string, phoneNumber: string, status: 'verified' | 'disabled'): Promise<{ success: boolean; caller_ids: any[] }> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.patch(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/caller-ids`, {
      phone_number: phoneNumber,
      status
    }, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  async deleteCallerId(assistantId: string, phoneNumber: string): Promise<{ success: boolean; caller_ids: any[] }> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.delete(
      `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/caller-ids/${encodeURIComponent(phoneNumber)}`,
      { headers: { 'Authorization': `Bearer ${await userData.getIdToken()}` } }
    );
    return response.data;
  }

  // ============================================
  // OUTBOUND NUMBERS (400 numbers and client caller IDs, filtered by the server)
  // ============================================

  private async outboundNumbersHeaders(json = false): Promise<Record<string, string>> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    return {
      'Authorization': `Bearer ${await userData.getIdToken()}`,
      ...(json ? { 'Content-Type': 'application/json' } : {})
    };
  }

  async getOutboundNumbers(assistantId: string, fresh = false): Promise<OutboundNumbersView> {
    const response = await axios.get(`${this.url}/dashboard/agents/${assistantId}/outbound-numbers`, {
      headers: await this.outboundNumbersHeaders(),
      params: fresh ? { fresh: 1 } : undefined
    });
    return response.data;
  }

  /** Añade y quita varios números en una sola escritura. Lo que el servidor no acepta vuelve en `failed`. */
  async applyOutboundNumbers(
    assistantId: string,
    changes: { add?: OutboundNumberRef[]; remove?: OutboundNumberRef[] }
  ): Promise<OutboundNumbersBatchResult> {
    const response = await axios.post(`${this.url}/dashboard/agents/${assistantId}/outbound-numbers/batch`,
      changes,
      { headers: await this.outboundNumbersHeaders(true) });
    return response.data;
  }

  async removeOutboundNumber(assistantId: string, phoneNumber: string, kind: 'number' | 'caller_id'): Promise<OutboundNumbersView> {
    const response = await axios.delete(
      `${this.url}/dashboard/agents/${assistantId}/outbound-numbers/${encodeURIComponent(phoneNumber)}`,
      { headers: await this.outboundNumbersHeaders(), params: { kind } });
    return response.data;
  }

  /** Alta de un número propio: le llaman al número con el código que devuelve. */
  async startCallerIdVerification(assistantId: string, phoneNumber: string, friendlyName: string): Promise<CallerIdVerificationStart> {
    const response = await axios.post(`${this.url}/dashboard/agents/${assistantId}/outbound-numbers/verify`,
      { phone_number: phoneNumber, friendly_name: friendlyName },
      { headers: await this.outboundNumbersHeaders(true) });
    return response.data;
  }

  /** Tecleado el código, lo confirma, lo registra como suyo y lo añade al agente. */
  async confirmCallerIdVerification(assistantId: string, phoneNumber: string): Promise<CallerIdVerificationResult> {
    const response = await axios.post(`${this.url}/dashboard/agents/${assistantId}/outbound-numbers/verify/confirm`,
      { phone_number: phoneNumber },
      { headers: await this.outboundNumbersHeaders(true) });
    return response.data;
  }

  /** Otros agentes de la misma cuenta a los que se pueden copiar los números de este. */
  async getOutboundCopyTargets(assistantId: string): Promise<{ success: boolean; agents: CopyTarget[] }> {
    const response = await axios.get(`${this.url}/dashboard/agents/${assistantId}/outbound-numbers/targets`, {
      headers: await this.outboundNumbersHeaders()
    });
    return response.data;
  }

  /** Copia los caller IDs en rotación de este agente a otros (aditivo: no quita nada a los destinos). */
  async copyOutboundNumbers(assistantId: string, targetIds: string[]): Promise<CopyOutcome> {
    const response = await axios.post(`${this.url}/dashboard/agents/${assistantId}/outbound-numbers/copy`,
      { target_ids: targetIds },
      { headers: await this.outboundNumbersHeaders(true) });
    return response.data;
  }

  /** Interruptor de cuenta «solo 400». Solo funciona para el equipo de Meetzy (el servidor lo comprueba). */
  async setAccountOnly400(userId: string, only400: boolean): Promise<{ success: boolean; user_id: string; only_400: boolean }> {
    const response = await axios.put(`${this.url}/dashboard/admin/outbound-policy/${userId}`,
      { only_400: only400 },
      { headers: await this.outboundNumbersHeaders(true) });
    return response.data;
  }

  // ============================================
  // WHATSAPP CONFIG (ElevenLabs native WhatsApp — admin only)
  // ============================================

  async updateWhatsappConfig(assistantId: string, config: { whatsapp_agent_id?: string; whatsapp_phone_number_id?: string }): Promise<{ success: boolean; whatsapp_agent_id?: string; whatsapp_phone_number_id?: string }> {
    const userData = await this.authService.getUserData();
    if (!userData) throw new Error('User not authenticated');
    const response = await axios.patch(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/whatsapp-config`, config, {
      headers: { 'Authorization': `Bearer ${await userData.getIdToken()}`, 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  // ============================================
  // KNOWLEDGE BASE
  // ============================================

  /**
   * Get knowledge base documents for an assistant
   */
  async getKnowledgeBase(assistantId: string): Promise<{ documents: any[] }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error getting knowledge base:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Create a knowledge base document from URL
   */
  async createKnowledgeBaseUrl(
    assistantId: string, 
    url: string, 
    name?: string, 
    usageMode: 'prompt' | 'auto' = 'auto'
  ): Promise<{ success: boolean; document: any }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(
          `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base/url`,
          { url, name, usage_mode: usageMode },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error creating URL document:', error);
        throw error.response?.data || error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Create a knowledge base document from text
   */
  async createKnowledgeBaseText(
    assistantId: string, 
    text: string, 
    name?: string, 
    usageMode: 'prompt' | 'auto' = 'auto'
  ): Promise<{ success: boolean; document: any }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(
          `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base/text`,
          { text, name, usage_mode: usageMode },
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error creating text document:', error);
        throw error.response?.data || error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Create a knowledge base document from file
   */
  async createKnowledgeBaseFile(
    assistantId: string, 
    file: File, 
    name?: string, 
    usageMode: 'prompt' | 'auto' = 'auto'
  ): Promise<{ success: boolean; document: any }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const formData = new FormData();
        formData.append('files', file); // 'files' is the field name expected by multer middleware
        if (name) {
          formData.append('name', name);
        }
        formData.append('usage_mode', usageMode);

        const response = await axios.post(
          `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base/file`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error creating file document:', error);
        throw error.response?.data || error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Delete a knowledge base document
   */
  async deleteKnowledgeBaseDocument(assistantId: string, documentId: string): Promise<{ success: boolean }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.delete(
          `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base/${documentId}`,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error deleting document:', error);
        throw error.response?.data || error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Update a knowledge base document
   * Can update: usage_mode, url (for URL docs), text (for text docs)
   */
  async updateKnowledgeBaseDocument(
    assistantId: string, 
    documentId: string,
    updates: {
      usage_mode?: 'prompt' | 'auto';
      url?: string;
      text?: string;
      name?: string;
    }
  ): Promise<{ success: boolean; document: any }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.patch(
          `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base/${documentId}`,
          updates,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error updating document:', error);
        throw error.response?.data || error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Get full content and details of a knowledge base document
   * Used for editing documents (returns content for text, url for URL documents)
   */
  async getKnowledgeBaseDocumentContent(
    assistantId: string,
    documentId: string
  ): Promise<{ success: boolean; content: string; url?: string; name?: string; type?: string }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(
          `${this.url}/dashboard/advanced-editor/assistant/${assistantId}/knowledge-base/${documentId}/content`,
          {
            headers: {
              'Authorization': `Bearer ${await userData.getIdToken()}`
            }
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error getting document content:', error);
        throw error.response?.data || error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  // ============================================
  // BILLY CONSUMPTION
  // ============================================

  async getCurrentBillyConsumption(filters: BillyConsumptionFilters = {}): Promise<BillyConsumptionResponse> {
    const userData = await this.authService.getUserData();
    if (!userData) {
      throw new Error('User not authenticated');
    }

    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate && filters.endDate) {
        queryParams.set('start_date', filters.startDate);
        queryParams.set('end_date', filters.endDate);
      }
      const query = queryParams.toString();
      const url = `${this.url}/dashboard/billy-consumption/current${query ? `?${query}` : ''}`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error getting Billy consumption:', error);
      throw error.response?.data || error;
    }
  }

  async getBillyBillingHistory(): Promise<BillyBillingHistoryResponse> {
    const userData = await this.authService.getUserData();
    if (!userData) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await axios.get(`${this.url}/dashboard/billy-consumption/history`, {
        headers: {
          'Authorization': `Bearer ${await userData.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error getting Billy billing history:', error);
      throw error.response?.data || error;
    }
  }

  // ============================================
  // STRIPE BILLING & SUBSCRIPTIONS
  // ============================================

  /**
   * Create a Stripe Checkout session for subscription
   */
  async createCheckoutSession(priceId: string, isAnnual: boolean = false): Promise<{ url: string }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/billing/create-checkout-session`, {
          priceId,
          isAnnual
        }, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus(): Promise<any> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.get(`${this.url}/billing/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error getting subscription status:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /**
   * Create customer portal session for managing subscription
   */
  async createCustomerPortalSession(): Promise<{ url: string }> {
    const userData = await this.authService.getUserData();
    if (userData) {
      try {
        const response = await axios.post(`${this.url}/billing/create-portal-session`, {}, {
          headers: {
            'Authorization': `Bearer ${await userData.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        console.error('Error creating portal session:', error);
        throw error;
      }
    } else {
      throw new Error('User not authenticated');
    }
  }

  /** USER EVENTS (Activity Tracking) */

  async getUserEvents(params: {
    email?: string;
    event_type?: string;
    from?: string;
    to?: string;
    exclude_admins?: boolean;
    search?: string;
    member_id?: string;
    account_id?: string;
    page_path?: string;
    button_detail?: string;
    sort_by?: string;
    sort_dir?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<any> {
    await this.refreshAuthToken();
    const queryParams = new URLSearchParams();
    if (params.email) queryParams.set('email', params.email);
    if (params.event_type) queryParams.set('event_type', params.event_type);
    if (params.from) queryParams.set('from', params.from);
    if (params.to) queryParams.set('to', params.to);
    if (params.exclude_admins) queryParams.set('exclude_admins', 'true');
    if (params.search) queryParams.set('search', params.search);
    if (params.member_id) queryParams.set('member_id', params.member_id);
    if (params.account_id) queryParams.set('account_id', params.account_id);
    if (params.page_path) queryParams.set('page_path', params.page_path);
    if (params.button_detail) queryParams.set('button_detail', params.button_detail);
    if (params.sort_by) queryParams.set('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.set('sort_dir', params.sort_dir);
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));

    // Let the caller handle/display errors so the UI can distinguish
    // "no results" from "the request failed".
    const response = await axios.get(
      `${this.url}/dashboard/user-events?${queryParams.toString()}`,
      {
        headers: {
          Authorization: 'Bearer ' + this.authToken
        }
      }
    );
    return response.data;
  }

  /**
   * Distinct pages/actions available for the User Activity filter dropdowns.
   */
  async getUserEventFilters(): Promise<{ pages: { value: string; count: number }[]; actions: { value: string; count: number }[] }> {
    await this.refreshAuthToken();
    const response = await axios.get(
      `${this.url}/dashboard/user-events/filters`,
      {
        headers: {
          Authorization: 'Bearer ' + this.authToken
        }
      }
    );
    return response.data;
  }

  /**
   * Get available pricing plans
   */
  async getPricingPlans(): Promise<any[]> {
    try {
      const response = await axios.get(`${this.url}/billing/plans`);
      return response.data;
    } catch (error: any) {
      console.error('Error getting pricing plans:', error);
      throw error;
    }
  }

}
